/**
 * Cost-preview dialog (the product pillar: exact call counts, estimated
 * dollars, always — DESIGN §2.1, api.md §4). Titled "Refresh the map?" with
 * "Refresh now" / "Not now" (owner consistency round 2026-08-25 — the
 * dialog asks a question, the buttons answer it). Numbers render VERBATIM
 * from `/refresh/preview`; call counts are labeled exact, dollars estimated.
 * Confirm posts the refresh job and hands off to the BACKGROUND tracker
 * (refreshJobsStore → the global RefreshChip): the dialog closes at once,
 * the user keeps using the app, and the map fills in live — no modal.
 */

import { Link } from 'react-router';
import { useCreateJob, useRefreshPreview } from '../../api/queries';
import type { Ulid } from '../../api/types';
import { formatUsd, plural } from '../../lib/format';
import { Button, Dialog, ErrorNote, Spinner } from '../../lib/ui';
import { useRefreshJobs } from './refreshJobsStore';

/**
 * Mounted only while open (2026-09-07): the query observer inside then mounts
 * fresh on every open, so `refetchOnMount: 'always'` fires and a preview
 * that failed last time (a 500, a flaky fetch) is asked for again instead of
 * the dialog sitting on "Comparing your code against the map…" for good.
 */
export function CostPreviewDialog(props: {
  repoId: Ulid;
  open: boolean;
  onClose: () => void;
}) {
  if (!props.open) return null;
  return <CostPreviewDialogBody {...props} />;
}

function CostPreviewDialogBody({
  repoId,
  open,
  onClose,
}: {
  repoId: Ulid;
  open: boolean;
  onClose: () => void;
}) {
  const preview = useRefreshPreview(repoId, open);
  const createJob = useCreateJob(repoId);

  const close = () => {
    onClose();
  };

  // A failed POST stays in the dialog: `createJob.isError` draws it below,
  // and nothing here rejects into the void.
  const confirm = () => {
    createJob.mutate(
      { kind: 'refresh' },
      {
        onSuccess: ({ job_id }) => {
          // Fully background from here: the global chip carries the progress.
          useRefreshJobs.getState().track(repoId, job_id);
          close();
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      onSubmit={() => {
        if (preview.data !== undefined && preview.data.key_resolves !== false && !createJob.isPending) {
          confirm();
        }
      }}
      title="Refresh the map?"
    >
      {preview.isPending ? (
        <div className="py-6 text-center">
          <Spinner label="Comparing your code against the map…" />
        </div>
      ) : preview.isError ? (
        <ErrorNote>Preview failed: {preview.error.message}</ErrorNote>
      ) : (
        <div className="space-y-4" data-testid="cost-preview">
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2">
            <dt className="text-secondary">Commits behind</dt>
            <dd className="m-0 text-primary">{preview.data.commits_behind ?? 'n/a'}</dd>

            {preview.data.delta != null && preview.data.delta.total_changed > 0 ? (
              <>
                <dt className="text-secondary">Files changed</dt>
                <dd className="m-0 text-primary" data-testid="delta-files">
                  {preview.data.delta.total_changed}
                  {preview.data.delta.changed_node_ids.length > 0 ? (
                    <span className="text-tertiary">
                      {' '}
                      across {plural(preview.data.delta.changed_node_ids.length, 'area')}
                    </span>
                  ) : null}
                </dd>
              </>
            ) : null}

            <dt className="text-secondary">Areas to re-narrate</dt>
            <dd className="m-0 text-primary">
              {preview.data.narrate.stale} of{' '}
              {preview.data.narrate.stale + preview.data.narrate.cached}{' '}
              <span className="text-tertiary">({preview.data.narrate.cached} cached)</span>
            </dd>

            <dt className="text-secondary">Flows to re-trace</dt>
            <dd className="m-0 text-primary">
              {preview.data.flows.stale} of {preview.data.flows.stale + preview.data.flows.cached}
              {preview.data.flows.catalog_rediscovery ? (
                <span className="text-tertiary"> · full catalog rediscovery</span>
              ) : null}
            </dd>

            <dt className="text-secondary">API calls</dt>
            <dd className="m-0 text-primary">
              {preview.data.estimated_calls}{' '}
              <span className="rounded-chip border border-default px-1 text-[length:var(--text-micro-size)] text-tertiary">
                exact
              </span>
            </dd>

            <dt className="text-secondary">Cost</dt>
            <dd className="m-0 text-primary">
              {formatUsd(preview.data.estimated_cost_usd.low)} –{' '}
              {formatUsd(preview.data.estimated_cost_usd.high)}{' '}
              <span className="rounded-chip border border-default px-1 text-[length:var(--text-micro-size)] text-tertiary">
                estimated
              </span>
            </dd>
          </dl>

          {preview.data.delta != null && !preview.data.delta.linear ? (
            <p className="text-(--shifted)" data-testid="history-rewritten">
              The branch history was rewritten since loremap last synced (a
              rebase, a squash, or an amended commit), so the commit this map
              was built from is no longer on the branch. loremap compares from
              the last commit both versions share
              {preview.data.delta.merge_base != null
                ? ` (${preview.data.delta.merge_base.slice(0, 7)})`
                : ''}
              , and re-reads everything that looks different since then.
            </p>
          ) : null}

          {preview.data.narrate.reason === 'model_changed' ? (
            <p className="text-secondary" data-testid="invalidation-reason">
              Everything re-narrates because the model changed
              {preview.data.narrate.previous_model != null
                ? ` (was ${preview.data.narrate.previous_model})`
                : ''}
              : narration is written per model, so none of the old text carries
              over. Switch the model back in Settings to keep the existing map.
            </p>
          ) : preview.data.narrate.reason === 'narration_engine_updated' ? (
            <p className="text-secondary" data-testid="invalidation-reason">
              Everything re-narrates because the narrator itself was updated
              (new prompt or project framing), not because your code changed.
            </p>
          ) : preview.data.narrate.reason === 'first_run' ? (
            <p className="text-secondary" data-testid="invalidation-reason">
              First narration of this codebase: every area gets written once,
              and later refreshes only pay for what changed.
            </p>
          ) : null}

          {preview.data.narrate.stale_node_ids.length > 0 ? (
            <details className="text-secondary">
              <summary className="cursor-pointer">
                {plural(preview.data.narrate.stale_node_ids.length, 'changed area')}
              </summary>
              <ul className="mono mt-2 max-h-40 list-none overflow-auto p-0">
                {preview.data.narrate.stale_node_ids.map((id) => (
                  <li key={id} className="py-0.5">
                    {id}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          {preview.data.priority != null && preview.data.priority.phase1_calls > 0 ? (
            <p className="text-secondary" data-testid="priority-split">
              Usable early: {preview.data.priority.phase1_calls} priority areas + flows narrate
              first (the map opens then); the remaining{' '}
              {preview.data.priority.phase2_calls - preview.data.priority.renarrated_later} fill
              in behind.
              {preview.data.priority.renarrated_later > 0 ? (
                <span className="text-tertiary" data-testid="priority-renarrate-note">
                  {' '}
                  {preview.data.priority.renarrated_later} of the priority areas get a second,
                  fuller pass once their children exist: +
                  {formatUsd(preview.data.priority.extra_cost_usd.low)}–
                  {formatUsd(preview.data.priority.extra_cost_usd.high)} on top of the total
                  above.
                </span>
              ) : null}
            </p>
          ) : null}

          <p className="text-tertiary">
            Your key, your bill: nothing runs until you confirm.
          </p>

          <ErrorNote>{createJob.isError ? createJob.error.message : null}</ErrorNote>

          <div className="flex justify-end gap-2">
            <Button kind="ghost" onClick={close}>
              Not now
            </Button>
            {preview.data.key_resolves !== false ? (
              <Button kind="primary" disabled={createJob.isPending} onClick={confirm}>
                {createJob.isPending ? 'Starting…' : 'Refresh now'}
              </Button>
            ) : (
              // No key resolves for this run (audit 2026-08-26): the primary
              // action can only fail — offer the fix instead.
              <Link
                to="/settings"
                data-testid="preview-add-key"
                className="inline-block h-8 rounded-card border border-transparent bg-(--accent) px-3 leading-8 font-medium text-(--bg-canvas) no-underline hover:opacity-90"
              >
                Add your key first
              </Link>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}
